<template>
  <article class="page--loan">
    <div id="loanPageSubHeader">
      <div class="grid-container">
        <div class="grid-x">
          <div class="cell auto">
            <p v-if="loan" id="loanCountry">
              <FontAwesomeIcon icon="map-marker-alt" class="country-icon" />
              <span class="country">{{ loan.entrepreneur.mfi.country }}</span>
            </p>
          </div>
          <div class="cell shrink">
            <vbLink v-if="$device.isMedium" :to="searchRoute" class="link-to-search">
              <span v-html="$t('linkToSearch')" />
            </vbLink>
          </div>
        </div>
      </div>
    </div>
    <Loader id="loanPrefetcher" centered :loading="$queue.isRunning">
      <template v-if="loan">
        <LoanDetails id="loanDetails" />

        <div class="grid-container full-until-medium">
          <Card class="loan-repayments-card">
            <CardSection>
              <LoanRepayments id="loanRepayments" :bufferRepayment="loan.bufferRepayment" :repayments="loan.repayments" />
            </CardSection>
          </Card>

          <Card v-if="updates.length" class="updates-card">
            <CardSection>
              <div id="updates" class="grid-container" :class="{ full: !$device.isMedium }">
                <div class="grid-x">
                  <div class="cell">
                    <h2 v-html="$t('updateGridHeading')" />
                  </div>
                </div>
                <BlockGrid :items="updates">
                  <UpdateBlockGridItem slot-scope="{ item }" :item="item" />
                </BlockGrid>
              </div>
            </CardSection>
          </Card>

          <Card class="lenders-card">
            <CardSection>
              <div id="lenders" class="grid-container" :class="{ full: !$device.isMedium }">
                <div class="grid-x">
                  <div class="cell">
                    <h2 v-html="$t('lenderGridHeading', { name })" />
                  </div>
                </div>
                <LenderGrid id="lenderGrid" :lenders="lenders" />
              </div>
            </CardSection>
            <template v-if="teams.length">
              <hr class="margin-top-0">
              <CardSection>
                <div id="teams" class="grid-container">
                  <div class="grid-x">
                    <div class="cell">
                      <h2 v-html="$t('teamGridHeading', { name })" />
                    </div>
                  </div>
                  <TeamGrid id="teamGrid" :teams="teams" />
                </div>
              </CardSection>
            </template>
          </Card>
        </div>
      </template>
    </Loader>
  </article>
</template>

<script>
  import BlockGrid from '~/components/frontend/block-grid'
  import i18n from './locales'
  import LenderGrid from '~/modules/users/components/frontend/lender-grid'
  import LoanDetails from '~/modules/loans/components/frontend/loan-details'
  import LoanImage from '~/modules/loans/entities/frontend/loan-image'
  import LoanRepayments from '~/modules/loans/components/frontend/loan-details-repayments'
  import LoanUpdate from '~/modules/updates/entities/update'
  import LoanUpdates from '~/modules/updates/components/frontend/loan-updates'
  import Page from '~/components/frontend/page'
  import TeamGrid from '~/modules/teams/components/frontend/team-grid'
  import UpdateBlockGridItem from '~/modules/updates/components/frontend/block-grid-item'
  import UsesQueue from '~/libs/queue/mixins/uses-queue'
  import { Card, CardSection } from '~/components/frontend/ui/card'
  import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons/faMapMarkerAlt'
  import { getEntrepreneurName } from '~/modules/entrepreneurs/utils'
  import { getPageTitle } from '~/utils/framework'
  import { mapGetters } from 'vuex'

  export default {
    name: 'PageLoan',
    extends: Page,
    i18n,
    mixins: [
      UsesQueue,
    ],
    components: {
      BlockGrid,
      Card,
      CardSection,
      LenderGrid,
      LoanDetails,
      LoanRepayments,
      LoanUpdates,
      TeamGrid,
      UpdateBlockGridItem,
    },
    data () {
      return {
        searchRoute: {
          name: 'loans:frontend:search',
        },
      }
    },
    head() {
      const name = this.subheader.heading
      const title = getPageTitle.call(this, name)

      return {
        title: title,
        meta: [
          { hid: 'og:title',
            property: 'og:title',
            content: name
          },
          {
            hid: 'og:url',
            property: 'og:url',
            content: this.baseUrl+this.$route.path
          },
          { hid: 'og:image',
            property: 'og:image',
            content: this.ogImageUrl
          },
        ]
      }
    },
    computed: {
      ...mapGetters({
        loan: 'loans/current',
      }),
      ogImageUrl() {
        if (!this.loan) return null

        const mainImage = this.$get(this.loan, 'images', []).find(image => image.isMain)
        return this.$get(mainImage, 'openGraph', null)
      },
      subheader () {
        return {
          heading: this.$get(this.loan, 'entrepreneur.displayName', this.$t('heading')),
          extraClasses: 'loan-sub-header',
        }
      },
      lenders () {
        return this.loan.lenders
      },
      name () {
        return getEntrepreneurName(this.loan.entrepreneur, { firstOnly: true })
      },
      teams () {
        return this.$get(this.loan, 'teams', [])
      },
      updates () {
        const updates = this.$get(this.loan, 'updates', [])

        if (Array.isArray(updates)) {
          return updates.filter(item => item instanceof LoanUpdate)
        }

        return null
      },
    },
    methods: {
      queue () {
        this.$queue.addJob({
          action: 'loans/fetch',
          payload: {
            loanId: this.$route.params.loanId,
          },
        })
      },
    },
    beforeCreate () {
      this.$store.commit('loans/SET_LOAN', null)
    },
    created () {
      this.$fa.add(faMapMarkerAlt)
    },
  }
</script>

<style lang="scss" scoped>
  @import "~assets/scss/frontend/_colours";
  @import "~assets/scss/frontend/_settings";
  @import "~assets/scss/frontend/foundation/_settings";
  @import "~foundation-sites/scss/util/_breakpoint";
  @import "~assets/scss/frontend/_card";

  .page--loan {
    padding-bottom: 3rem;

    .loader__spinner {
      margin-top: 2rem;
    }

    #loanPageSubHeader {
      background-color: $colour-green;
      color: $colour-white;

      @each $breakpoint, $paddings in $subheader-padding {
        @if map-has-key($paddings, bottom) {
          @include breakpoint ($breakpoint) {
            padding-bottom: map-get($paddings, bottom);
          }
        }
      }

      #loanCountry {
        margin-bottom: 0;

        .country-icon {
          margin-right: 0.25rem;
        }
        .country {
          text-transform: capitalize;
        }
      }
    }

    @include card-borderless-on-small();

    .link-to-search {
      color: $colour-white;
      display: block;

      &:hover {
        color: darken($colour-white, 10%);
      }
    }

    .loan-repayments-card {
      @include breakpoint (medium) {
        margin-top: 2rem;
      }
    }

    .updates-card {
      margin-top: 2rem;

      h2 {
        margin-top: 1rem;
        text-align: center;
      }
    }

    .lenders-card {
      margin-top: 2rem;

      #lenders,
      #teams {
        h2 {
          margin-top: 1rem;
          text-align: center;
        }
      }

      #lenders #lenderGrid,
      #teams #teamGrid {
        margin-top: 1rem;

        @include breakpoint (medium) {
          margin-top: 2rem;
        }
      }
    }
  }
</style>
